/**
 * Fronteira de erro por rota de plugin.
 *
 * Um plugin que quebra a desenhar mostra um cartao contido, em portugues, e o
 * resto do aplicativo continua a funcionar. Sem isto, um erro nas financas leva
 * consigo os lembretes da medicacao — que e exatamente o que nao pode acontecer.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Banner, BigButton, Card, Stack } from '@flos/ui';

export interface PluginErrorBoundaryProps {
  /** Nome do plugin em portugues, para a mensagem nomear o que falhou. */
  pluginName: string;
  onError?: (error: Error, info: ErrorInfo) => void;
  onRecover?: () => void;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class PluginErrorBoundary extends Component<PluginErrorBoundaryProps, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Nunca engolir em silencio: vai para o diagnostico com contexto.
    this.props.onError?.(error, info);
  }

  private readonly retry = (): void => {
    this.setState({ error: null });
    this.props.onRecover?.();
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <Card tone="danger" title={`${this.props.pluginName} teve um problema`}>
        <Stack gap="md">
          <Banner tone="danger" title="Esta parte não abriu">
            O resto do aplicativo continua a funcionar. Pode tentar outra vez ou
            voltar ao início.
          </Banner>
          <BigButton onClick={this.retry} icon="🔄" fullWidth>
            Tentar outra vez
          </BigButton>
        </Stack>
      </Card>
    );
  }
}
